<template>
  <div v-if="visible" ref="rootRef" class="preloader">
    <div ref="backdropRef" class="preloader__backdrop"></div>
    <div ref="lensRef" class="preloader__lens">
      <div class="preloader__nebula"></div>
      <div class="preloader__grain"></div>
    </div>
    <div ref="contentRef" class="preloader__content">
      <div class="preloader__logo-wrap">
        <img
          ref="logoMarkRef"
          src="/Imgs/digitalizando.svg"
          alt=""
          class="preloader__logo"
        />
      </div>
      <div class="preloader__progress-track">
        <div ref="progressBarRef" class="preloader__progress-fill"></div>
      </div>
      <span ref="counterRef" class="preloader__counter">0</span>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from "vue";
import { gsap } from "gsap";
import { usePreloader } from "../composables/usePreloader";

const emit = defineEmits(["complete"]);

const { state, setProgress, markAssetsLoaded, markLensComplete } =
  usePreloader();

const visible = ref(true);
const rootRef = ref(null);
const backdropRef = ref(null);
const lensRef = ref(null);
const contentRef = ref(null);
const logoMarkRef = ref(null);
const progressBarRef = ref(null);
const counterRef = ref(null);

let ctx;

const ASSETS = ["/Imgs/Astronauta.png", "/Imgs/digitalizando.svg"];

function preloadAssets() {
  return new Promise((resolve) => {
    let loaded = 0;
    const total = ASSETS.length;

    if (total === 0) {
      setProgress(100);
      resolve();
      return;
    }

    ASSETS.forEach((src) => {
      const img = new Image();
      img.onload = img.onerror = () => {
        loaded++;
        const pct = Math.round((loaded / total) * 100);
        setProgress(pct);
        if (loaded >= total) resolve();
      };
      img.src = src;
    });
  });
}

function animateCounter(target) {
  const obj = { val: parseFloat(counterRef.value?.textContent || "0") };
  return gsap.to(obj, {
    val: target,
    duration: 0.4,
    ease: "power2.out",
    onUpdate() {
      if (counterRef.value) {
        counterRef.value.textContent = Math.round(obj.val);
      }
    },
  });
}

function runLensTransition() {
  const tl = gsap.timeline({
    onComplete() {
      markLensComplete();
      emit("complete");
      setTimeout(() => {
        visible.value = false;
      }, 100);
    },
  });

  tl.to(contentRef.value, {
    autoAlpha: 0,
    scale: 0.8,
    filter: "blur(20px)",
    duration: 0.6,
    ease: "power3.in",
  });

  tl.to(
    backdropRef.value,
    {
      scale: 1.5,
      filter: "blur(40px) brightness(2.5) contrast(1.8)",
      duration: 1.2,
      ease: "expo.in",
    },
    "-=0.3"
  );

  tl.to(
    lensRef.value,
    {
      clipPath: "circle(150% at 50% 50%)",
      duration: 1.4,
      ease: "expo.inOut",
    },
    "-=1.0"
  );

  tl.to(
    rootRef.value,
    {
      autoAlpha: 0,
      duration: 0.3,
      ease: "power2.out",
    },
    "-=0.3"
  );
}

onMounted(async () => {
  document.documentElement.style.overflow = "hidden";

  ctx = gsap.context(() => {
    gsap.set(lensRef.value, {
      clipPath: "circle(0% at 50% 50%)",
    });

    gsap.fromTo(
      logoMarkRef.value,
      { autoAlpha: 0, scale: 0.6, filter: "blur(15px)" },
      {
        autoAlpha: 1,
        scale: 1,
        filter: "blur(0px)",
        duration: 1.2,
        ease: "expo.out",
        delay: 0.2,
      }
    );

    gsap.fromTo(
      progressBarRef.value,
      { scaleX: 0 },
      { scaleX: 0, duration: 0 }
    );
  }, rootRef.value);

  const stopWatch = watch(
    () => state.progress,
    (val) => {
      if (!progressBarRef.value) return;
      gsap.to(progressBarRef.value, {
        scaleX: val / 100,
        duration: 0.5,
        ease: "power2.out",
      });
      animateCounter(val);
    }
  );

  await preloadAssets();
  markAssetsLoaded();

  await gsap.to({}, { duration: 0.6 });

  stopWatch();
  runLensTransition();
});

onUnmounted(() => {
  if (ctx) ctx.revert();
  document.documentElement.style.overflow = "";
});
</script>

<style scoped>
.preloader {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #020208;
}

.preloader__backdrop {
  position: absolute;
  inset: 0;
  background: radial-gradient(
      ellipse at 35% 40%,
      rgba(167, 139, 250, 0.1) 0%,
      transparent 50%
    ),
    radial-gradient(
      ellipse at 65% 60%,
      rgba(96, 165, 250, 0.08) 0%,
      transparent 50%
    ),
    #020208;
  will-change: transform, filter;
}

.preloader__lens {
  position: absolute;
  inset: 0;
  clip-path: circle(0% at 50% 50%);
  will-change: clip-path;
  z-index: 1;
}

.preloader__nebula {
  position: absolute;
  inset: -40%;
  background: radial-gradient(
      ellipse at 30% 30%,
      rgba(167, 139, 250, 0.15) 0%,
      transparent 50%
    ),
    radial-gradient(
      ellipse at 70% 70%,
      rgba(96, 165, 250, 0.12) 0%,
      transparent 50%
    ),
    radial-gradient(
      ellipse at 50% 20%,
      rgba(251, 191, 36, 0.06) 0%,
      transparent 40%
    );
  animation: preloaderNebula 8s ease-in-out infinite alternate;
}

.preloader__grain {
  position: absolute;
  inset: 0;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
  background-repeat: repeat;
  background-size: 256px 256px;
  opacity: 0.04;
  pointer-events: none;
}

@keyframes preloaderNebula {
  0% {
    transform: translate(0, 0) scale(1) rotate(0deg);
  }
  100% {
    transform: translate(20px, -15px) scale(1.08) rotate(2deg);
  }
}

.preloader__content {
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  will-change: transform, opacity, filter;
}

.preloader__logo-wrap {
  width: clamp(200px, 40vw, 400px);
  display: flex;
  align-items: center;
  justify-content: center;
}

.preloader__logo {
  width: 100%;
  filter: brightness(1.5) contrast(1.1);
  visibility: hidden;
}

.preloader__progress-track {
  width: clamp(180px, 30vw, 300px);
  height: 2px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 2px;
  overflow: hidden;
  position: relative;
}

.preloader__progress-fill {
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, var(--color-accent), #60a5fa);
  transform-origin: left center;
  transform: scaleX(0);
  border-radius: 2px;
  will-change: transform;
}

.preloader__progress-fill::after {
  content: "";
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--color-accent);
  box-shadow: 0 0 12px var(--color-accent-glow),
    0 0 30px var(--color-accent-glow);
}

.preloader__counter {
  font-family: var(--font-display);
  font-size: clamp(0.75rem, 1.2vw, 0.9rem);
  font-weight: 300;
  color: rgba(255, 255, 255, 0.25);
  letter-spacing: 0.3em;
  font-variant-numeric: tabular-nums;
}
</style>
